import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Languages, LifeBuoy } from "lucide-react";
import { BrandLogo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/lib/api";
import { cn } from "@/lib/utils";
import { COPY, LANGS } from "./ReferCopy";
import { ReferForm } from "./ReferForm";
import { ReferHelpdesk } from "./ReferHelpdesk";
import { ReferTrack } from "./ReferTrack";
import { ReferVerify } from "./ReferVerify";

/** The public employee referral form. No session, no cookie, no token — the same kind of door as
 *  /apply. Everybody starts at the gate; once past it there are two tabs, one to send a referral
 *  and one to follow the ones already sent. Written for a phone: one column, big taps. */
export default function Refer() {
  const [lang, setLang] = useState("en");
  const t = COPY[lang] || COPY.en;

  const [config, setConfig] = useState(null);
  const [me, setMe] = useState(null);          // verified {emp_id, name, role, branch, phone}
  const [tab, setTab] = useState("make");
  const [help, setHelp] = useState(false);
  const [helpWho, setHelpWho] = useState(null);  // whoever was picked at the gate, for the dialog
  const [sent, setSent] = useState(0);         // bumped on a send, so Track reloads

  const openHelp = (who) => { setHelpWho(who || null); setHelp(true); };

  useEffect(() => {
    axios.get(`${API_BASE}/public/refer/config`)
      .then(({ data }) => setConfig(data))
      .catch(() => setConfig({ roles: [], experience: [], branches: [] }));
  }, []);

  const tabBtn = (key, label) => (
    <button
      type="button" onClick={() => setTab(key)} data-testid={`refer-tab-${key}`}
      className={cn("flex-1 rounded-lg px-3 py-2.5 text-[13px] font-semibold transition-colors",
        tab === key ? "bg-emerald-600 text-white shadow-sm"
          : "text-muted-foreground hover:bg-muted")}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="refer-page">
      <header className="sticky top-0 z-10 border-b bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-2 px-3 py-3 sm:px-4">
          <BrandLogo className="h-7 shrink-0 sm:h-8" />
          <div className="flex items-center gap-1.5 sm:gap-2">
            <Languages className="hidden h-4 w-4 text-muted-foreground sm:block" />
            <select
              value={lang} onChange={(e) => setLang(e.target.value)}
              data-testid="refer-language"
              className="h-9 rounded-lg border bg-card px-2 text-[13px] font-medium outline-none"
            >
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <Button
              variant="outline" size="sm"
              className="h-9 gap-1.5 border-emerald-600/40 px-2.5 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-600/10 dark:text-emerald-400"
              onClick={() => openHelp(me)} data-testid="refer-helpdesk-btn"
            >
              <LifeBuoy className="h-3.5 w-3.5" />
              {t.helpdesk}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-3 py-6 sm:px-4 sm:py-7">
        <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-4xl"
          data-testid="refer-title">{t.title}</h1>
        <p className="mt-1.5 text-[13px] text-muted-foreground sm:text-sm">{t.subtitle}</p>

        {!me ? (
          <div className="mt-5">
            <ReferVerify t={t} config={config} onVerified={setMe} onHelpdesk={openHelp} />
          </div>
        ) : (
          <>
            <div className="mt-5 flex gap-1.5 rounded-xl border bg-card p-1.5"
              data-testid="refer-tabs">
              {tabBtn("make", t.tabMake)}
              {tabBtn("track", t.tabTrack)}
            </div>
            <div className="mt-4">
              {tab === "make" ? (
                <ReferForm t={t} me={me} config={config} onSent={() => setSent((n) => n + 1)} />
              ) : (
                <ReferTrack t={t} me={me} reloadKey={sent} />
              )}
            </div>
          </>
        )}

        <div className="mt-6 text-center">
          <Link to="/login" className="text-xs text-muted-foreground underline"
            data-testid="refer-back-signin">{t.backToSignIn}</Link>
        </div>
      </main>

      <ReferHelpdesk t={t} open={help} onOpenChange={setHelp} me={me || helpWho} />
    </div>
  );
}
